import {
  CalendarTypes,
  type CalendarEvent,
  type StationBrand,
  type StationEvents,
  type CalendarEventSchedule,
} from '@centric-os/types';
import { DateTime, Duration } from 'luxon';
import type {
  DayHeaderContentArg,
  EventApi,
  EventContentArg,
  EventInput,
} from '@fullcalendar/core';

import { computed } from 'vue';

const enum CDLCalendarDurationUnits {
  MINUTES = 'minutes',
  HOURS = 'hours',
  DAYS = 'days',
  WEEKS = 'weeks',
}

type EventTemplateInput = {
  event: Pick<EventApi, 'title' | 'display' | 'startStr' | 'endStr' | 'extendedProps'>;
};

/** Station colours follow the Pantry tag variants, the same six slots as tags and legends. */
export const STATION_COLOR_SLOT_COUNT = 6;

// Whole literal class names so Tailwind can see them; tailwind.css also forces them via @source inline.
const STATION_SLOT_CLASSES: readonly string[] = [
  'bg-tag-variant-1-background text-tag-variant-1-text',
  'bg-tag-variant-2-background text-tag-variant-2-text',
  'bg-tag-variant-3-background text-tag-variant-3-text',
  'bg-tag-variant-4-background text-tag-variant-4-text',
  'bg-tag-variant-5-background text-tag-variant-5-text',
  'bg-tag-variant-6-background text-tag-variant-6-text',
];

export const getStationSlotClasses = (slot?: number): string => {
  const index =
    typeof slot === 'number' && Number.isInteger(slot) && slot >= 1
      ? (slot - 1) % STATION_COLOR_SLOT_COUNT
      : 0;
  return STATION_SLOT_CLASSES[index];
};

/** The station's wall clock for an instant, relabelled as UTC: the frame the calendar grid renders in. */
const toStationWallClock = (millis: number, timezone?: string): DateTime =>
  DateTime.fromMillis(millis, { zone: timezone ?? 'local' }).setZone('utc', {
    keepLocalTime: true,
  });

export default () => {
  const customCalendarOptionsForStationEvents = computed(() => {
    return {
      timeZone: 'UTC',
      initialView: 'timeGridWeek',
      weekends: true,
      slotDuration: '1:00:00',
      slotEventOverlap: false,
      expandRows: true,
      height: 'auto',
      headerToolbar: false,
      allDaySlot: false,
      dayHeaderFormat: { weekday: 'long', day: 'numeric' },
      eventOrderStrict: true,
      eventTimeFormat: {
        hour: 'numeric',
        minute: '2-digit',
        meridiem: true,
      },
      events: [] as EventInput[],
      eventContent(info: EventContentArg) {
        return {
          html: getEventTemplate(info),
        };
      },
      dayHeaderContent(info: DayHeaderContentArg) {
        return {
          html: getDayHeaderContent(info),
        };
      },
    };
  });
  const getEventDateStart = (schedule: CalendarEventSchedule, timezone?: string) => {
    if (!schedule?.start) return 'No start date provided';

    const start = toStationWallClock(schedule.start, timezone);

    const times = schedule?.times?.[0] || start.toFormat('HH:mm');
    const [hours, minutes] = times.split(':');
    return start
      .startOf('day')
      .plus({ hours: parseInt(hours), minutes: parseInt(minutes) })
      .toISO();
  };

  const getEventDateEnd = (schedule: CalendarEventSchedule, timezone?: string) => {
    if (schedule?.end) return toStationWallClock(schedule.end, timezone).endOf('day');
    if (schedule?.year && schedule?.month && schedule?.dayOfMonth) {
      const { year, month, dayOfMonth } = schedule;
      return DateTime.fromObject({
        year: year[0],
        month: month[0] + 1,
        day: dayOfMonth[0],
      }).setZone('utc');
    }
    return undefined;
  };
  const getEventDuration = (schedule: CalendarEventSchedule) => {
    if (schedule?.start && schedule?.end && !schedule?.durationUnit)
      return schedule?.end - schedule?.start;
    const durationUnit = schedule?.durationUnit;
    if (!durationUnit) return 86400000; // One day
    if (durationUnit === CDLCalendarDurationUnits.MINUTES)
      return Duration.fromObject({ minutes: schedule?.duration }).as('milliseconds');
    if (durationUnit === CDLCalendarDurationUnits.HOURS)
      return Duration.fromObject({ hours: schedule?.duration }).as('milliseconds');
    if (durationUnit === CDLCalendarDurationUnits.DAYS)
      return Duration.fromObject({ days: schedule?.duration }).as('milliseconds');
    if (durationUnit === CDLCalendarDurationUnits.WEEKS)
      return Duration.fromObject({ weeks: schedule?.duration }).as('milliseconds');
  };
  const getSingleStationsEvents = (
    locationCalendarEvents: StationEvents = {},
    station: StationBrand,
    timezone?: string,
  ) => {
    const stationEvents = locationCalendarEvents?.[station?.id];
    const menuIds = Object.keys(stationEvents?.menuEvents || {});
    const calendarEvents = menuIds.reduce((acc, id) => {
      if (stationEvents.menuEvents?.[id]?.length) {
        stationEvents.menuEvents[id].forEach((menuEvent) => {
          const newEvent = buildEvent({
            calendarEvent: menuEvent,
            station: station,
            timezone,
          });
          acc.push(newEvent);
        });
      }
      return acc;
    }, []);
    locationCalendarEvents?.[station?.id]?.closureEvents.forEach((closureEvent: CalendarEvent) => {
      const newEvent = buildEvent({
        calendarEvent: closureEvent,
        station: station,
        timezone,
      });
      calendarEvents.push(newEvent);
    });
    return calendarEvents;
  };

  const getMultipleStationsEvents = (
    locationCalendarEvents: StationEvents = {},
    currentStation: StationBrand,
    allStations: StationBrand[],
    timezone?: string,
  ) => {
    // @ts-expect-error FIXME: Variable 'allEvents' implicitly has type 'any[]' in some locations where its ...
    const allEvents = [];
    // @ts-expect-error FIXME: Variable 'allEvents' implicitly has an 'any[]' type.
    if (!locationCalendarEvents) return allEvents;
    for (const [stationId, stationEvents] of Object.entries(locationCalendarEvents)) {
      const station = allStations.find((stationBrand) => stationBrand?.id === stationId);
      if (station?.id === currentStation?.id) {
        locationCalendarEvents?.[station?.id]?.closureEvents.forEach(
          (closureEvent: CalendarEvent) => {
            const newEvent = buildEvent({ calendarEvent: closureEvent, station, timezone });
            allEvents.push(newEvent);
          },
        );
      }
      for (const [menuId, menuEvents] of Object.entries(stationEvents.menuEvents)) {
        if (menuEvents) {
          menuEvents.forEach((menuEvent) => {
            const newEvent = buildEvent({
              calendarEvent: menuEvent,
              station,
              timezone,
            });

            allEvents.push(newEvent);
          });
        }
      }
    }

    // @ts-expect-error FIXME: Variable 'allEvents' implicitly has an 'any[]' type.
    return allEvents;
  };

  const buildEvent = ({
    calendarEvent,
    station,
    timezone,
  }: {
    calendarEvent: CalendarEvent;
    station?: StationBrand;
    timezone?: string;
  }) => {
    const data = calendarEvent?.data;
    const schedule = calendarEvent?.schedule;
    const byWeekDay = schedule?.dayOfWeek?.map((day) => (day === 0 ? 6 : day - 1)); // FullCal: MON == 0. CDL Cal: SUN == 0
    const isClosure = data.calendar === CalendarTypes.HOLIDAY;
    const event: { [key: string]: any } = {
      title: data?.title,
      className: isClosure ? 'station-closure' : '',
      display: isClosure ? 'background' : '',
      // extended props below
      colorSlot: station?.color?.slot,
      // @ts-expect-error FIXME: Property 'week' does not exist on type 'boolean | { week: number; }'.
      interval: schedule?.every?.week,
      menuEventDisabled: data?.is?.disabled,
      // Read here, not in the template, so toggling Show in app recomputes the events.
      hidden: Boolean(data?.is?.disabled || station?.is?.hidden),
      isPickup: data?.is?.pickup,
      isDelivery: data?.is?.delivery,
      menuId: data?.menu,
      repeats: byWeekDay || [0, 1, 2, 3, 4, 5, 6],
      event: { data, schedule },
      station: station,
    };

    if (isClosure && schedule?.start && schedule?.end) {
      event.start = toStationWallClock(schedule.start, timezone).toISO();
      event.end = toStationWallClock(schedule.end, timezone).toISO();
    } else {
      event.duration = getEventDuration(schedule);
      event.rrule = {
        byweekday: isClosure ? undefined : byWeekDay || [0, 1, 2, 3, 4, 5, 6],
        // @ts-expect-error FIXME: Property 'week' does not exist on type 'boolean | { week: number; }'.
        interval: schedule?.every?.week || 1,
        freq: 'weekly',
        dtstart: getEventDateStart(schedule, timezone),
        until: getEventDateEnd(schedule, timezone),
      };
    }

    return event;
  };
  const getStationColor = (isActiveStation: boolean, station: StationBrand, colorIndex: number) => {
    // Slot 1 is reserved for the viewed station; the others cycle through 2..6.
    const slot = isActiveStation ? 1 : (colorIndex % (STATION_COLOR_SLOT_COUNT - 1)) + 2;
    const background = `var(--color-tag-variant-${slot}-background)`;
    return {
      enabledColor: background,
      disabledColor: background,
      ringColor: `var(--color-tag-variant-${slot}-text)`,
      slot,
    };
  };
  const getDayHeaderContent = ({ text }: Pick<DayHeaderContentArg, 'text'>): string => {
    const [date, weekday = ''] = text.split(' ');
    return `<div class="cos-calendar-day-header"><span class="cos-calendar-day-header__date">${date}</span><span class="cos-calendar-day-header__weekday">${weekday}</span></div>`;
  };

  const formatEventTime = (time: DateTime) => time.toFormat('h:mma').toLowerCase();

  const getEventTemplate = ({ event }: EventTemplateInput): string => {
    const start = DateTime.fromISO(event.startStr).setZone('utc');
    const end = DateTime.fromISO(event.endStr).setZone('utc');
    const timeRange = `${formatEventTime(start)} – ${formatEventTime(end)}`;

    if (event.display === 'background') {
      const allDay = start.hour === 0 && start.minute === 0 && end.hour === 0 && end.minute === 0;
      return `<div class="cos-calendar-event cos-calendar-event--closure">
        <div class="cos-calendar-event__summary">
          <p class="cos-calendar-event__title">${event.title}</p>
          <p class="cos-calendar-event__time">Station closure - ${allDay ? 'All day' : timeRange}</p>
        </div>
      </div>`;
    }

    const { colorSlot, interval, hidden } = event.extendedProps;
    const repeat =
      interval > 1
        ? `<div class="cos-calendar-event__repeat"><i class="material-symbols-rounded cos-calendar-event__icon" aria-hidden="true">sync</i><span>${interval} weeks</span></div>`
        : '';

    return `<div class="cos-calendar-event ${getStationSlotClasses(colorSlot)}">
      <div class="cos-calendar-event__summary">
        <i class="material-symbols-rounded cos-calendar-event__icon" aria-hidden="true">${hidden ? 'visibility_off' : 'visibility'}</i>
        <p class="cos-calendar-event__title">${event.title}</p>
        <p class="cos-calendar-event__time">${timeRange}</p>
      </div>${repeat}
    </div>`;
  };

  function getEventTimesDuration(event: CalendarEvent, timezone?: string) {
    const duration = getEventDuration(event?.schedule);
    const startTime = DateTime.fromISO(getEventDateStart(event?.schedule, timezone)).setZone('utc');
    const endTime = startTime.plus({ millisecond: duration });
    return `${startTime.toFormat('h:mm a').toLowerCase()} - ${endTime
      .toFormat('h:mm a')
      .toLowerCase()}`;
  }

  function getEventDates(menuEvent: CalendarEvent, timezone?: string) {
    const startTime = DateTime.fromISO(getEventDateStart(menuEvent?.schedule, timezone)).setZone(
      'utc',
    );
    const endTime = getEventDateEnd(menuEvent?.schedule, timezone);
    return `${startTime.toFormat('MMMM d, yyyy')} - ${
      endTime?.toFormat('MMMM d, yyyy') || 'No end date'
    }`;
  }
  /**
   * Converts AM/PM to lowercase and adds a space between the time and AM/PM if there is none
   * @param timeDate a string containing a time and date
   * @returns returns the time and date with AM/PM in lowercase and if there is no space between the time and AM/PM, it adds a space (eg. 'January 1, 2022 1:00PM to January 1, 2022 1:00 pm ')
   */
  function toLowerCaseAMPM(timeDate: string): string {
    return timeDate.replace(/(\d)(AM|PM)|\bAM\b|\bPM\b/g, (match, digit, meridiem) => {
      if (digit && meridiem) {
        return `${digit} ${meridiem.toLowerCase()}`;
      }
      return match.toLowerCase();
    });
  }

  /**
   * Get the date + time range for an event as a string, in the station's timezone
   * @param event the event to get the date + time range for
   * @param timezone the station's timezone; the browser's zone when omitted
   * @returns the range of date + time for the event (eg. 'January 1, 2022 1:00 pm - January 2, 2022 2:00 pm')
   */
  function getEventDateTimes(event: CalendarEvent, timezone?: string): string {
    const formatDateTime = (millis: number): string => {
      const dateTime = DateTime.fromMillis(millis, { zone: timezone ?? 'local' })
        .toFormat('MMMM d, yyyy h:mma')
        .replace(/(\d)(AM|PM)/g, '$1 $2');
      return toLowerCaseAMPM(dateTime);
    };

    const startDateTime = formatDateTime(event.schedule.start);
    const endDateTime = event.schedule.end ? formatDateTime(event.schedule.end) : 'No end date';

    return `${startDateTime} - ${endDateTime}`;
  }

  return {
    getEventDateStart,
    getEventDateEnd,
    getEventDuration,
    getSingleStationsEvents,
    getMultipleStationsEvents,
    getEventTemplate,
    getDayHeaderContent,
    getEventTimesDuration,
    getEventDates,
    getEventDateTimes,
    getStationColor,
    customCalendarOptionsForStationEvents,
    toLowerCaseAMPM,
  };
};
